import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import Select from "../components/Select";
import { SWEETALERT_THEME } from "../utils/sweetalert-theme";
import ScenariosPage from "./Scenarios";
import scenarioOptionMeta from "../data/scenarioOptionMeta.json";
import InfoTooltip from "../components/InfoTooltip";

export default function Pathway() {
  const {
    authFetch,
    accessToken,
    bootstrapping,
    user,
    pendingTask,
    pendingTaskState,
    startPendingTask,
  } = useAuth();

  // data
  const [jsonData, setJsonData] = useState(null);
  const [country, setCountry] = useState(""); // backend: req.user.state
  const [taskid] = useState(4);

  // ✅ pathways guardados en BD
  const [userPathways, setUserPathways] = useState([]);
  const [customScenarios, setCustomScenarios] = useState([]);

  // UI state
  const [loadingPage, setLoadingPage] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // form state
  const [pathwayName, setPathwayName] = useState("");
  const [selectValues, setSelectValues] = useState({});
  const [showPresetScenarios, setShowPresetScenarios] = useState(false);
  const [showCreateCustomModal, setShowCreateCustomModal] = useState(false);
  const [createCustomTab, setCreateCustomTab] = useState(null);

  const scenarioNamesRef = useRef([]);

  const scenarios = Array.isArray(jsonData?.scenarios)
    ? jsonData.scenarios
    : [];

  const FORCED_SCENARIO_VALUES = {
    year: "2020",
    tradebalance: "No",
  };

  const getForcedScenarioValue = (scenarioName) => {
    const key = String(scenarioName || "")
      .toLowerCase()
      .replace(/[^a-z]/g, "");
    return FORCED_SCENARIO_VALUES[key] ?? null;
  };

  const normalizedType = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z]/g, "");

  const categoryOrder = ["foodDemand", "foodProduction", "landAndEnvironment"];

  const categoryTitles = {
    foodDemand: "Food Demand Scenarios",
    foodProduction: "Food Production Scenarios",
    landAndEnvironment: "Land and Environment Scenarios",
    other: "Other Scenarios",
  };

  const getScenarioCategory = (label) => {
    const v = normalizedType(label);

    if (v.includes("populationactivity")) return "other";

    // Food Demand Scenarios
    if (v.includes("population")) return "foodDemand";
    if (v.includes("diet")) return "foodDemand";
    if (v.includes("importshare")) return "foodDemand";
    if (v.includes("exportevolution")) return "foodDemand";
    if (v.includes("biofuel") && v.includes("demand")) return "other";
    if (v.includes("foodwaste")) return "foodDemand";

    // Food Production Scenarios
    if (v.includes("postharvestlosses")) return "foodProduction";
    if (v.includes("cropproductivity")) return "foodProduction";
    if (v.includes("irrigationarea")) return "foodProduction";
    if (
      v.includes("livestockproductivity") ||
      v.includes("livestockproducticity")
    )
      return "foodProduction";
    if (v.includes("livestocksystem")) return "foodProduction";
    if (v.includes("ruminantdensity") || v.includes("rumaintdensity"))
      return "foodProduction";

    // Land and Environment Scenarios
    if (v.includes("landforexpansion")) return "landAndEnvironment";
    if (v.includes("urbanexpansion")) return "landAndEnvironment";
    if (v.includes("afforestation")) return "landAndEnvironment";
    if (v.includes("protectedareas") || v.includes("protectedarea"))
      return "landAndEnvironment";
    if (v.includes("agroecologicalpractices")) return "landAndEnvironment";

    return "other";
  };

  const isDietScenario = (label) => normalizedType(label).includes("diet");
  const isPopulationScenario = (label) =>
    normalizedType(label) === "population";
  const isImportShareScenario = (label) =>
    normalizedType(label) === "importshare";
  const isLivestockProductivityScenario = (label) =>
    normalizedType(label).includes("livestockproductiv");
  const isCropProductivityScenario = (label) =>
    normalizedType(label).includes("cropproductivity");
  const isIrrigationScenario = (label) =>
    normalizedType(label).includes("irrigation");
  const isProtectedAreasScenario = (label) => {
    const normalizedLabel = normalizedType(label);
    return (
      normalizedLabel.includes("protected") && normalizedLabel.includes("area")
    );
  };
  const isAfforestationScenario = (label) =>
    normalizedType(label).includes("afforest");
  const isExportEvolutionScenario = (label) => {
    const normalizedLabel = normalizedType(label);
    return (
      normalizedLabel.includes("export") &&
      normalizedLabel.includes("evolution")
    );
  };
  const getScenarioOptionMeta = (scenarioName) => {
    const typeKey = scenarioTypeFromLabel(scenarioName);
    if (scenarioOptionMeta[typeKey]) return scenarioOptionMeta[typeKey];

    const normalizedTypeKey = String(typeKey).toLowerCase();
    const matchedKey = Object.keys(scenarioOptionMeta).find((key) => {
      const normalizedKey = String(key).toLowerCase();
      return (
        normalizedKey === normalizedTypeKey ||
        normalizedTypeKey.includes(normalizedKey) ||
        normalizedKey.includes(normalizedTypeKey)
      );
    });
    return matchedKey ? scenarioOptionMeta[matchedKey] : null;
  };

  const getScenarioDescription = (scenarioName) => {
    const typeKey = scenarioTypeFromLabel(scenarioName);
    const descMap = scenarioOptionMeta._descriptions || {};
    const normalizedTypeKey = normalizedType(typeKey);
    const matchedKey = Object.keys(descMap).find((key) => {
      const normalizedKey = normalizedType(key);
      return (
        normalizedKey === normalizedTypeKey ||
        normalizedTypeKey.includes(normalizedKey) ||
        normalizedKey.includes(normalizedTypeKey)
      );
    });
    return descMap[matchedKey] || descMap[typeKey] || "";
  };

  const getScenarioTitle = (scenarioName) => {
    const typeKey = scenarioTypeFromLabel(scenarioName);
    const titleMap = scenarioOptionMeta._titles || {};
    const normalizedTypeKey = normalizedType(typeKey);
    const matchedTitleKey = Object.keys(titleMap).find((key) => {
      const normalizedKey = normalizedType(key);
      return (
        normalizedKey === normalizedTypeKey ||
        normalizedTypeKey.includes(normalizedKey) ||
        normalizedKey.includes(normalizedTypeKey)
      );
    });

    return (
      titleMap[matchedTitleKey] ||
      titleMap[typeKey] ||
      window?.i18nTranslations?.pathway?.[scenarioName] ||
      scenarioName
    );
  };

  const getScenarioOptionLabel = (scenarioName, optionValue) => {
    const meta = getScenarioOptionMeta(scenarioName);
    const normalizedValue = String(optionValue ?? "");
    return meta?.labels?.[normalizedValue] || normalizedValue;
  };

  const normalizeScenarioOption = (option) => {
    if (typeof option === "string" || typeof option === "number") {
      return { value: String(option), label: String(option), description: "" };
    }
    if (!option || typeof option !== "object") {
      return { value: "", label: "", description: "" };
    }
    return {
      value: String(option.value ?? option.label ?? ""),
      label: String(option.label ?? option.value ?? ""),
      description: String(option.description ?? option.desc ?? ""),
    };
  };

  const CREATE_CUSTOM_LABEL = (
    <span className="font-semibold">+ Create Custom</span>
  );

  const getScenarioSelectOptions = (scenario) => {
    const meta = getScenarioOptionMeta(scenario.name);
    const baseOptions = (scenario.options || []).map((option) => {
      const normalizedOption = normalizeScenarioOption(option);
      if (!meta) return normalizedOption;
      return {
        ...normalizedOption,
        label: meta.labels?.[normalizedOption.value] || normalizedOption.label,
        description:
          normalizedOption.description ||
          meta.descriptions?.[normalizedOption.value] ||
          "",
        anchor: meta.quantitative_anchors?.[normalizedOption.value] || "",
      };
    });

    if (isDietScenario(scenario.name)) {
      return [
        { value: "__customize_diet__", label: CREATE_CUSTOM_LABEL },
        ...baseOptions,
      ];
    }

    if (isPopulationScenario(scenario.name)) {
      return [
        { value: "__customize_population__", label: CREATE_CUSTOM_LABEL },
        ...baseOptions,
      ];
    }

    if (isImportShareScenario(scenario.name)) {
      return [
        { value: "__customize_import_share__", label: CREATE_CUSTOM_LABEL },
        ...baseOptions,
      ];
    }

    if (isLivestockProductivityScenario(scenario.name)) {
      return [
        {
          value: "__customize_livestock_productivity__",
          label: CREATE_CUSTOM_LABEL,
        },
        ...baseOptions,
      ];
    }

    if (isCropProductivityScenario(scenario.name)) {
      return [
        {
          value: "__customize_crop_productivity__",
          label: CREATE_CUSTOM_LABEL,
        },
        ...baseOptions,
      ];
    }

    if (isIrrigationScenario(scenario.name)) {
      return [
        { value: "__customize_irrigation__", label: CREATE_CUSTOM_LABEL },
        ...baseOptions,
      ];
    }

    if (isProtectedAreasScenario(scenario.name)) {
      return [
        { value: "__customize_protected_areas__", label: CREATE_CUSTOM_LABEL },
        ...baseOptions,
      ];
    }

    if (isAfforestationScenario(scenario.name)) {
      return [
        { value: "__customize_afforestation__", label: CREATE_CUSTOM_LABEL },
        ...baseOptions,
      ];
    }

    if (isExportEvolutionScenario(scenario.name)) {
      return [
        { value: "__customize_export_evolution__", label: CREATE_CUSTOM_LABEL },
        ...baseOptions,
      ];
    }

    return baseOptions;
  };

  const handleScenarioChange = (label, value) => {
    if (isDietScenario(label) && value === "__customize_diet__") {
      setCreateCustomTab(1);
      setShowCreateCustomModal(true);
      return;
    }

    if (isPopulationScenario(label) && value === "__customize_population__") {
      setCreateCustomTab(6);
      setShowCreateCustomModal(true);
      return;
    }

    if (
      isImportShareScenario(label) &&
      value === "__customize_import_share__"
    ) {
      setCreateCustomTab(2);
      setShowCreateCustomModal(true);
      return;
    }

    if (
      isLivestockProductivityScenario(label) &&
      value === "__customize_livestock_productivity__"
    ) {
      setCreateCustomTab(7);
      setShowCreateCustomModal(true);
      return;
    }

    if (
      isCropProductivityScenario(label) &&
      value === "__customize_crop_productivity__"
    ) {
      setCreateCustomTab(8);
      setShowCreateCustomModal(true);
      return;
    }

    if (isIrrigationScenario(label) && value === "__customize_irrigation__") {
      setCreateCustomTab(10);
      setShowCreateCustomModal(true);
      return;
    }

    if (
      isProtectedAreasScenario(label) &&
      value === "__customize_protected_areas__"
    ) {
      setCreateCustomTab(4);
      setShowCreateCustomModal(true);
      return;
    }

    if (
      isAfforestationScenario(label) &&
      value === "__customize_afforestation__"
    ) {
      setCreateCustomTab(5);
      setShowCreateCustomModal(true);
      return;
    }

    if (
      isExportEvolutionScenario(label) &&
      value === "__customize_export_evolution__"
    ) {
      setCreateCustomTab(3);
      setShowCreateCustomModal(true);
      return;
    }

    setSelectValues((prev) => ({ ...prev, [label]: value }));
  };

  const scenarioTypeFromLabel = (label) => {
    const v = normalizedType(label);
    if (v.includes("diet")) return "diet";
    if (v.includes("import") && v.includes("share")) return "importshare";
    if (v.includes("export") && v.includes("evolution"))
      return "exportevolution";
    if (v.includes("export")) return "exportevolution";
    if (v.includes("protected") || v.includes("proteg"))
      return "protectedareas";
    if (v.includes("afforest") || v.includes("forest")) return "afforestation";
    if (v.includes("irrigation")) return "irrigationarea";
    return v;
  };

  const normalizeScenarioType = (typeScenario) => {
    const v = normalizedType(typeScenario);
    if (!v) return "";
    if (v.includes("import") && v.includes("share")) return "importshare";
    if (v === "imports" || v === "import") return "importshare";
    if (v.includes("export") && v.includes("evolution"))
      return "exportevolution";
    if (v === "exports" || v === "export") return "exportevolution";
    if (v.includes("protected") || v.includes("proteg"))
      return "protectedareas";
    if (v.includes("afforest") || v.includes("forest")) return "afforestation";
    if (v.includes("irrigation")) return "irrigationarea";
    return v;
  };

  // Enriquecer opciones base con escenarios custom del usuario por tipo
  const scenariosWithCustomOptions = useMemo(() => {
    const byType = new Map();

    for (const row of Array.isArray(customScenarios) ? customScenarios : []) {
      const t = normalizeScenarioType(row?.type_scenario);
      const n = String(row?.name || "").trim();
      if (!t || !n) continue;
      if (!byType.has(t)) byType.set(t, new Set());
      byType.get(t).add(n);
    }

    return (Array.isArray(scenarios) ? scenarios : []).map((sc) => {
      const typeKey = scenarioTypeFromLabel(sc?.name);
      const customNames = Array.from(byType.get(typeKey) || []);
      const base = Array.isArray(sc?.options) ? sc.options : [];
      const merged = [...new Set([...base, ...customNames])];
      return {
        ...sc,
        options: merged,
      };
    });
  }, [scenarios, customScenarios]);

  const visibleScenarios = useMemo(
    () =>
      scenariosWithCustomOptions.filter(
        (scenario) => getForcedScenarioValue(scenario.name) === null,
      ),
    [scenariosWithCustomOptions],
  );

  const groupedVisibleScenarios = useMemo(() => {
    const grouped = {
      foodDemand: [],
      foodProduction: [],
      landAndEnvironment: [],
      other: [],
    };

    for (const scenario of visibleScenarios) {
      const category = getScenarioCategory(scenario.name);
      grouped[category] = [...(grouped[category] || []), scenario];
    }

    return grouped;
  }, [visibleScenarios]);

  const presetScenarios = useMemo(
    () =>
      scenariosWithCustomOptions.filter((scenario) => {
        const isForced = getForcedScenarioValue(scenario.name) !== null;
        const isOther = getScenarioCategory(scenario.name) === "other";
        return isForced || isOther;
      }),
    [scenariosWithCustomOptions],
  );

  // ✅ Lista combinada (JSON + BD) sin duplicados por name
  const pathwaysList = useMemo(() => {
    const fromJson = Array.isArray(jsonData?.Pathways) ? jsonData.Pathways : [];
    const fromDb = Array.isArray(userPathways) ? userPathways : [];

    const seen = new Set();
    const merged = [];

    // prioriza BD
    for (const p of fromDb) {
      const n = String(p?.name ?? "").trim();
      if (!n || seen.has(n)) continue;
      seen.add(n);
      merged.push(p);
    }
    for (const p of fromJson) {
      const n = String(p?.name ?? "").trim();
      if (!n || seen.has(n)) continue;
      seen.add(n);
      merged.push(p);
    }

    return merged;
  }, [jsonData, userPathways]);

  // ---------------------------------------
  // ✅ Carga defined scenarios + pathways BD
  // ---------------------------------------
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    async function load() {
      try {
        setLoadingPage(true);
        setError("");

        if (bootstrapping) return;

        const res = await authFetch("/api/pathways/definedscenarios", {
          method: "GET",
          signal: controller.signal,
        });

        if (res.status === 401) {
          throw new Error("Session expired. Please sign in again.");
        }

        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || `Error ${res.status}`);

        const loc = data.location || "";
        setCountry(loc);

        const payload =
          data?.jsonData?.[String(loc).trim().toUpperCase()] ?? null;

        if (!alive) return;

        setJsonData(payload);
        setUserPathways(Array.isArray(data?.pathways) ? data.pathways : []);
        setCustomScenarios(
          Array.isArray(data?.customScenarios) ? data.customScenarios : [],
        );

        const loadedScenarios = payload?.scenarios ?? [];
        scenarioNamesRef.current = loadedScenarios.map((s) => s.name);
        console.log(
          "Pathways loaded scenario names:",
          loadedScenarios.map((s) => s.name),
        );

        // restore local storage
        const savedInput = localStorage.getItem("input_pathway");
        setPathwayName(savedInput || "");

        const initialSelects = {};
        for (const s of loadedScenarios) {
          const key = `select_${s.name.replace(/\s+/g, "_")}`;
          const saved = localStorage.getItem(key);
          const defaultVal =
            Array.isArray(s.options) && s.options.length ? s.options[0] : "";
          const forcedVal = getForcedScenarioValue(s.name);
          initialSelects[s.name] = forcedVal ?? saved ?? defaultVal;
        }
        setSelectValues(initialSelects);
      } catch (e) {
        if (e?.name !== "AbortError")
          setError(e?.message || "Unexpected error");
      } finally {
        if (alive && !bootstrapping) setLoadingPage(false);
      }
    }

    load();

    return () => {
      alive = false;
      controller.abort();
    };
  }, [authFetch, bootstrapping]);

  // localStorage sync
  useEffect(() => {
    localStorage.setItem("input_pathway", pathwayName);
  }, [pathwayName]);

  useEffect(() => {
    for (const [name, val] of Object.entries(selectValues)) {
      const key = `select_${name.replace(/\s+/g, "_")}`;
      localStorage.setItem(key, val ?? "");
    }
  }, [selectValues]);

  // autocomplete selects when pathway changes
  useEffect(() => {
    if (!jsonData) return;
    const selectedPathway = pathwaysList.find((p) => p?.name === pathwayName);
    if (!selectedPathway) return;

    setSelectValues((prev) => {
      const next = { ...prev };
      for (const [key, value] of Object.entries(selectedPathway)) {
        if (!key) continue;
        const forcedVal = getForcedScenarioValue(key);
        if (forcedVal !== null) {
          next[key] = forcedVal;
          continue;
        }
        if (Object.prototype.hasOwnProperty.call(next, key)) {
          const sc = scenariosWithCustomOptions.find((s) => s.name === key);
          const opts = sc?.options ?? [];
          if (opts.includes(value)) next[key] = value;
        }
      }
      return next;
    });
  }, [pathwayName, jsonData, pathwaysList, scenariosWithCustomOptions]);

  // ---------------------------------------
  // ✅ helper: existe pathway con ese nombre?
  // ---------------------------------------
  const checkPathwayExists = useCallback(
    async ({ name, country }) => {
      const params = new URLSearchParams({ name, country });
      const res = await authFetch(`/api/pathways/exists?${params.toString()}`, {
        method: "GET",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Error ${res.status}`);
      return !!data?.exists;
    },
    [authFetch],
  );

  // ---------------------------------------
  // ✅ Encolar Pathway en tasktodo
  // ---------------------------------------
  const savePathwayViaTasktodo = useCallback(
    async ({ name, colvalue, countryForDb, overwrite }) => {
      const res = await authFetch("/api/sendtask/tasktodo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: taskid,
          name,
          status: "todo",
          onpc: 1,
          colname: "Pathway",
          colvalue,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (res.status === 401)
        throw new Error("Session expired. Please sign in again.");
      if (!res.ok)
        throw new Error(data?.error || `Error creating task (${res.status})`);

      const id = data?.id;
      if (!id) throw new Error("Backend did not return task id.");

      return {
        id,
        ok: true,
        country: countryForDb || country,
        overwrite: !!overwrite,
      };
    },
    [authFetch, taskid, country],
  );

  useEffect(() => {
    const onTaskCompleted = async (event) => {
      const detail = event?.detail || {};
      const taskMeta = detail?.pendingTask || {};
      if (taskMeta?.colname !== "Pathway") return;

      const saved = detail?.pathwaySaved?.pathway;
      if (saved) {
        const uiObj = {
          name: saved?.Name || taskMeta?.name || "",
          ...Object.fromEntries(
            (Array.isArray(saved?.Scenarios) ? saved.Scenarios : [])
              .map((it) => {
                const idx = Number(
                  String(it?.scenario || "").replace(/^S/i, ""),
                );
                const key = scenarios[idx]?.name;
                return [key, String(it?.value ?? "")];
              })
              .filter(([k]) => !!k),
          ),
          __id: detail?.pathwaySaved?.id,
          __updated_at: detail?.pathwaySaved?.updated_at,
        };

        setUserPathways((prev) => {
          const filtered = (Array.isArray(prev) ? prev : []).filter(
            (p) => p?.name !== uiObj.name,
          );
          return [uiObj, ...filtered];
        });

        if (uiObj.name) setPathwayName(uiObj.name);
      }

      localStorage.removeItem("input_pathway");
      (jsonData?.scenarios || []).forEach((scenario) => {
        localStorage.removeItem(`select_${scenario.name.replace(/\s+/g, "_")}`);
      });

      await Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Pathway processed and saved successfully.",
        confirmButtonColor: "#3085d6",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    };

    const onTaskFailed = async (event) => {
      const detail = event?.detail || {};
      const taskMeta = detail?.pendingTask || {};
      if (taskMeta?.colname !== "Pathway") return;

      const errMessage = detail?.error || "Error processing pathway task";
      setError(errMessage);

      await Swal.fire({
        icon: "error",
        title: "Task failed",
        text: errMessage,
        confirmButtonColor: "#3085d6",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    };

    window.addEventListener("scenathon-task-completed", onTaskCompleted);
    window.addEventListener("scenathon-task-failed", onTaskFailed);

    return () => {
      window.removeEventListener("scenathon-task-completed", onTaskCompleted);
      window.removeEventListener("scenathon-task-failed", onTaskFailed);
    };
  }, [jsonData, scenarios]);

  const insertData = async () => {
    setError("");

    if (bootstrapping) return;

    if (!accessToken) {
      setError("No active session. Please sign in again.");
      return;
    }

    if (!user?.id) {
      setError("Could not get userId from the session.");
      return;
    }

    const nameTrim = String(pathwayName || "").trim();
    if (!nameTrim) {
      await Swal.fire({
        icon: "warning",
        title: "Warning!",
        text: "Pathway is empty",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
        ...SWEETALERT_THEME,
      });
      return;
    }

    // 1) revisa duplicado y pregunta si reemplazar o cambiar nombre
    try {
      const exists = await checkPathwayExists({ name: nameTrim, country });

      let overwrite = false;

      if (exists) {
        const choice = await Swal.fire({
          icon: "warning",
          title: "Pathway already exists",
          text: `A pathway named "${nameTrim}" already exists. Do you want to replace it or change the name?`,
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Replace",
          denyButtonText: "Change name",
          cancelButtonText: "Cancel",
          allowOutsideClick: false,
          allowEscapeKey: false,
          ...SWEETALERT_THEME,
        });

        if (choice.isDenied) return; // usuario quiere cambiar el nombre
        if (!choice.isConfirmed) return; // cancel

        overwrite = true;
      }

      // 2) confirm general
      const confirm = await Swal.fire({
        title: "Are you sure?",
        text: "Do you want to submit your data?\nClick Confirm to continue or Cancel to review.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        ...SWEETALERT_THEME,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      if (!confirm.isConfirmed) return;

      // 3) encolar tarea
      setIsSubmitting(true);

      const scenariosArr = scenariosWithCustomOptions.map((s, index) => ({
        scenario: `S${index}`,
        value: String(
          getForcedScenarioValue(s.name) ?? selectValues[s.name] ?? "",
        ),
      }));

      const colvalue = {
        pathway: {
          Name: nameTrim,
          Scenarios: scenariosArr,
        },
      };

      const resp = await savePathwayViaTasktodo({
        name: nameTrim,
        colvalue,
        countryForDb: country,
        overwrite,
      });

      startPendingTask({
        id: resp.id,
        name: nameTrim,
        country: resp.country || country,
        colname: "Pathway",
        overwrite: resp.overwrite,
      });

      await Swal.fire({
        icon: "info",
        title: "Task sent",
        text: "Your pathway is being processed in background. You can navigate or refresh safely.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        ...SWEETALERT_THEME,
      });

      // ✅ Si quieres que se vea “de inmediato” en esta sección, NO redirijas.
      // window.location.href = "/resultados";
    } catch (err) {
      // si el backend dice que existe (carrera), también lo manejamos
      if (err?.code === "PATHWAY_EXISTS") {
        await Swal.fire({
          icon: "warning",
          title: "Pathway already exists",
          text: "That pathway already exists. Change the name or choose to replace it.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          ...SWEETALERT_THEME,
        });
        return;
      }
      console.error("Error guardando pathway:", err);
      setError(err?.message || "Error sending data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasPathwayTaskPending = pendingTask?.colname === "Pathway";
  const pathwayBusyLabel = hasPathwayTaskPending
    ? "Adding Pathway..."
    : "Launching Results...";

  return (
    <div
      id="app"
      className="
    mt-10
    w-full
    rounded-2xl
    bg-[#F8FAF9]
  "
    >
      <main className="flex-fill home">
        <div className="mx-auto w-full px-4 py-8 sm:px-6 lg:px-8">
          {/* =========================================================
          PATHWAY TASK PROCESSING
      ========================================================= */}
          {hasPathwayTaskPending && (
            <div
              className="
            mb-5
            rounded-xl
            border border-blue-200
            bg-blue-50
            px-5 py-4
            text-sm
            text-blue-900
            shadow-sm
          "
            >
              {`Pathway task #${pendingTask.id} is processing (${
                pendingTaskState || "pending"
              }). You can keep working while it finishes.`}

              <div
                className="
              mt-3
              flex
              w-full
              max-w-sm
              items-center
              gap-1
            "
                aria-hidden="true"
              >
                <span
                  className="
                h-1
                flex-1
                animate-pulse
                rounded-full
                bg-blue-300
              "
                />

                <span
                  className="
                h-1
                flex-1
                animate-pulse
                rounded-full
                bg-blue-400
              "
                  style={{ animationDelay: "0.15s" }}
                />

                <span
                  className="
                h-1
                flex-1
                animate-pulse
                rounded-full
                bg-blue-500
              "
                  style={{ animationDelay: "0.3s" }}
                />
              </div>
            </div>
          )}

          {/* =========================================================
          ERROR
      ========================================================= */}
          {error && (
            <div
              className="
            mb-5
            rounded-xl
            border border-red-200
            bg-red-50
            px-5 py-4
            text-sm
            text-red-800
            shadow-sm
          "
            >
              {error}
            </div>
          )}

          {/* =========================================================
          MAIN CONTENT
      ========================================================= */}
          <div className="tabs-container mb-4">
            {/* =======================================================
            PATHWAY NAME
        ======================================================= */}
            <div
              className="
            mb-7
            rounded-2xl
            border border-slate-200
            bg-white
            px-5
            py-5
            shadow-sm
          "
            >
              <label
                className="
              block
              text-center
              text-xs
              font-bold
              uppercase
              tracking-[0.14em]
              text-[#176B48]
            "
              >
                {window?.i18nTranslations?.pathway?.["pathway"] ||
                  "Pathway Name"}
              </label>

              <input
                type="text"
                className="
              mt-3
              w-full
              rounded-xl
              border border-slate-200
              bg-[#F8FAF9]
              px-4
              py-2.5
              text-sm
              text-slate-800
              outline-none
              transition-all
              duration-200
              placeholder:text-slate-400
              hover:border-slate-300
              focus:border-[#4C9772]
              focus:bg-white
              focus:ring-2
              focus:ring-[#4C9772]/15
            "
                placeholder="Type or select a pathway"
                list="datalist-pathways"
                value={pathwayName}
                onChange={(e) => setPathwayName(e.target.value)}
              />

              <datalist id="datalist-pathways">
                {(pathwaysList || []).map((p) => (
                  <option key={p.__id ?? p.name} value={p.name} />
                ))}
              </datalist>
            </div>

            {/* =======================================================
            OPTIONS
        ======================================================= */}
            <div id="optionsContainer" className="flex flex-col gap-6">
              {loadingPage ? (
                <>
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />

                  <SkeletonCard className="hidden lg:block" />
                  <SkeletonCard className="hidden lg:block" />
                </>
              ) : (
                <>
                  {/* =================================================
                  CATEGORY GRID
              ================================================= */}
                  <div
                    className="
                  grid
                  grid-cols-1
                  gap-6
                  xl:grid-cols-3
                  xl:items-start
                "
                  >
                    {categoryOrder.map((categoryKey) => {
                      const categoryScenarios =
                        groupedVisibleScenarios[categoryKey] || [];

                      if (!categoryScenarios.length) return null;

                      const isThirdColumn =
                        categoryKey === "landAndEnvironment";

                      /*
                       * Ethiopian inspired palette
                       *
                       * Food Demand:
                       * green
                       *
                       * Food Production:
                       * gold
                       *
                       * Land & Environment:
                       * red
                       */
                      const categoryTheme = {
                        foodDemand: {
                          header: "bg-[#DCE9DF]",
                          border: "border-[#A9C5B0]",
                          accent: "bg-[#17834B]",
                          text: "text-[#175C38]",
                          soft: "bg-[#F5F9F6]",
                        },

                        foodProduction: {
                          header: "bg-[#F7E9B8]",
                          border: "border-[#DFC870]",
                          accent: "bg-[#E2B522]",
                          text: "text-[#705B12]",
                          soft: "bg-[#FCFAF3]",
                        },

                        landAndEnvironment: {
                          header: "bg-[#F2D8D5]",
                          border: "border-[#D7A4A0]",
                          accent: "bg-[#C7463B]",
                          text: "text-[#7D302A]",
                          soft: "bg-[#FCF7F6]",
                        },
                      };

                      const theme =
                        categoryTheme[categoryKey] || categoryTheme.foodDemand;

                      return (
                        <section
                        key={categoryKey}
                        className={`
                          relative
                          overflow-visible
                          rounded-2xl
                          border
                          ${theme.border}
                          bg-white
                          shadow-sm
                          transition-all
                          duration-200
                          hover:shadow-md
                          focus-within:z-30
                        `}
                      >
                        {/* =========================================
                            CATEGORY HEADER
                        ========================================= */}
                        <div
                          className={`
                            overflow-hidden
                            rounded-t-[15px]
                            ${theme.header}
                          `}
                        >
                          <div
                            className={`
                              h-1.5
                              w-full
                              ${theme.accent}
                            `}
                          />

                          <div className="px-5 py-4">
                            <h4
                              className={`
                                text-center
                                text-[15px]
                                font-bold
                                tracking-wide
                                ${theme.text}
                              `}
                            >
                              {categoryTitles[categoryKey]}
                            </h4>
                          </div>
                        </div>

                        {/* =========================================
                            CATEGORY CONTENT
                        ========================================= */}
                        <div
                          className={`
                            rounded-b-2xl
                            p-4
                            pb-7
                            sm:p-5
                            sm:pb-7
                            ${theme.soft}
                          `}
                        >
                          <div
                            className="
                              flex
                              w-full
                              flex-col
                              gap-3
                            "
                          >
                            {categoryScenarios.map((scenario) => {
                              const label = scenario.name;
                              const title = getScenarioTitle(label);
                              const desc = getScenarioDescription(label);

                              return (
                                <div
                                  key={label}
                                  className="
                                    relative
                                    rounded-xl
                                    border
                                    border-slate-200
                                    bg-white
                                    px-4
                                    py-4
                                    shadow-[0_1px_2px_rgba(15,23,42,0.04)]
                                    transition-all
                                    duration-200
                                    hover:border-slate-300
                                    hover:shadow-sm
                                    focus-within:z-40
                                  "
                                >
                                  <div
                                    className="
                                      flex
                                      items-center
                                      justify-center
                                      gap-1.5
                                    "
                                  >
                                    <h5
                                      className="
                                        text-center
                                        text-sm
                                        font-semibold
                                        leading-5
                                        text-slate-800
                                      "
                                    >
                                      {title}
                                    </h5>

                                    {desc && (
                                      <InfoTooltip description={desc} />
                                    )}
                                  </div>

                                  <div className="relative z-10 mt-3">
                                    <Select
                                      value={selectValues[label] ?? ""}
                                      onChange={(v) =>
                                        handleScenarioChange(label, v)
                                      }
                                      options={getScenarioSelectOptions(scenario)}
                                      placeholder="Select..."
                                      tooltipPosition="left"
                                    />
                                  </div>
                                </div>
                              );
                            })}

                            {isThirdColumn && presetScenarios.length > 0 && (
                              <div
                                className="
                                  rounded-xl
                                  border
                                  border-dashed
                                  border-slate-300
                                  bg-white/80
                                  px-4
                                  py-4
                                "
                              >
                                <h5
                                  className="
                                    text-center
                                    text-sm
                                    font-semibold
                                    text-slate-800
                                  "
                                >
                                  Preset Scenarios
                                </h5>

                                <p
                                  className="
                                    mt-1
                                    text-center
                                    text-xs
                                    text-slate-500
                                  "
                                >
                                  View predefined scenario values
                                </p>

                                <button
                                  type="button"
                                  onClick={() => setShowPresetScenarios(true)}
                                  className="
                                    mt-3
                                    w-full
                                    rounded-lg
                                    border
                                    border-slate-300
                                    bg-white
                                    px-3
                                    py-2
                                    text-sm
                                    font-semibold
                                    text-slate-700
                                    transition-all
                                    hover:border-slate-400
                                    hover:bg-slate-50
                                  "
                                >
                                  View Presets
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </section>

                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* =========================================================
          PRESET SCENARIOS MODAL
      ========================================================= */}
          {showPresetScenarios && presetScenarios.length > 0 && (
            <div
              className="
              fixed
              inset-0
              z-[9999]
              flex
              items-center
              justify-center
              bg-black/45
              px-4
              py-6
              backdrop-blur-[1px]
            "
              onClick={() => setShowPresetScenarios(false)}
            >
              <div
                className="
                max-h-[85vh]
                w-full
                max-w-3xl
                overflow-y-auto
                rounded-2xl
                border
                border-slate-200
                bg-white
                p-6
                shadow-2xl
              "
                onClick={(e) => e.stopPropagation()}
              >
                {/* ===============================================
                  MODAL HEADER
              =============================================== */}
                <div
                  className="
                  mb-5
                  flex
                  items-start
                  justify-between
                  gap-3
                  border-b
                  border-slate-100
                  pb-4
                "
                >
                  <div>
                    <h3
                      className="
                      text-lg
                      font-semibold
                      text-slate-900
                    "
                    >
                      Preset Scenarios
                    </h3>

                    <p
                      className="
                      mt-1
                      text-sm
                      text-slate-500
                    "
                    >
                      These values are visible for reference only.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowPresetScenarios(false)}
                    className="
                    rounded-lg
                    border
                    border-slate-300
                    bg-white
                    px-3
                    py-2
                    text-sm
                    font-semibold
                    text-slate-700
                    transition
                    hover:bg-slate-50
                  "
                  >
                    Close
                  </button>
                </div>

                {/* ===============================================
                  PRESET GRID
              =============================================== */}
                <div
                  className="
                  grid
                  grid-cols-1
                  gap-4
                  sm:grid-cols-2
                "
                >
                  {presetScenarios.map((scenario) => {
                    const label = scenario.name;

                    const title = getScenarioTitle(label);

                    const forcedValue = getForcedScenarioValue(label);

                    const rawValue =
                      forcedValue ??
                      selectValues[label] ??
                      scenario.options?.[0] ??
                      "";

                    const displayValue = String(rawValue);

                    const displayLabel = getScenarioOptionLabel(
                      label,
                      rawValue,
                    );

                    return (
                      <div
                        key={label}
                        className="
                        rounded-xl
                        border
                        border-slate-200
                        bg-white
                        p-4
                        shadow-sm
                      "
                      >
                        <h5
                          className="
                          text-center
                          text-sm
                          font-semibold
                          text-slate-800
                        "
                        >
                          {title}
                        </h5>

                        <div
                          className="
                          mt-3
                          rounded-lg
                          border
                          border-slate-200
                          bg-slate-50
                          px-3
                          py-2.5
                          text-sm
                          font-medium
                          text-slate-700
                        "
                        >
                          {displayLabel || displayValue || "-"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* =========================================================
          CREATE CUSTOM SCENARIO MODAL
      ========================================================= */}
          {showCreateCustomModal && (
            <div
              className="
            fixed
            inset-0
            z-[10000]
            bg-black/50
            backdrop-blur-[1px]
          "
              onClick={() => {
                setShowCreateCustomModal(false);
                setCreateCustomTab(null);
              }}
            >
              <div
                className="
              absolute
              top-3
              right-3
              left-3
              max-h-[88vh]
              overflow-hidden
              rounded-2xl
              border
              border-slate-200
              bg-white
              shadow-2xl

              sm:top-6
              sm:right-6
              sm:left-6

              lg:top-10
              lg:right-10
              lg:left-[340px]
            "
                onClick={(e) => e.stopPropagation()}
              >
                {/* ===============================================
                MODAL HEADER
            =============================================== */}
                <div
                  className="
                flex
                items-center
                justify-between
                border-b
                border-slate-200
                bg-white
                px-5
                py-3.5
              "
                >
                  <div>
                    <h3
                      className="
                    text-base
                    font-semibold
                    text-slate-900
                  "
                    >
                      Scenario Definition
                    </h3>

                    <p
                      className="
                    mt-0.5
                    text-xs
                    text-slate-500
                  "
                    >
                      Create or modify scenario values
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateCustomModal(false);
                      setCreateCustomTab(null);
                    }}
                    className="
                  rounded-lg
                  border
                  border-slate-300
                  bg-white
                  px-3
                  py-1.5
                  text-sm
                  font-semibold
                  text-slate-700
                  transition
                  hover:bg-slate-50
                "
                  >
                    Close
                  </button>
                </div>

                {/* ===============================================
                SCENARIOS PAGE
            =============================================== */}
                <div
                  className="
                max-h-[calc(88vh-65px)]
                overflow-y-auto
                bg-[#F8FAF9]
              "
                >
                  <ScenariosPage
                    onlyTab={createCustomTab}
                    hideTabsHeader={true}
                    noTopMargin={true}
                  />
                </div>
              </div>
            </div>
          )}

          {/* =========================================================
          SUBMIT BUTTON
      ========================================================= */}
          <div
            className="
          mt-9
          flex
          justify-center
          border-t
          border-slate-200
          pt-7
        "
          >
            <button
              onClick={insertData}
              disabled={
                loadingPage ||
                bootstrapping ||
                isSubmitting ||
                hasPathwayTaskPending
              }
              className={`
            inline-flex
            min-w-[220px]
            items-center
            justify-center
            rounded-xl
            px-7
            py-3
            text-base
            font-semibold
            text-white
            shadow-md
            transition-all
            duration-200

            ${
              loadingPage ||
              bootstrapping ||
              isSubmitting ||
              hasPathwayTaskPending
                ? `
                    cursor-not-allowed
                    bg-slate-400
                    shadow-none
                  `
                : `
                    bg-[#176B48]
                    hover:-translate-y-[1px]
                    hover:bg-[#12583B]
                    hover:shadow-lg
                    active:translate-y-0
                    active:bg-[#104E35]
                  `
            }
          `}
            >
              {isSubmitting || hasPathwayTaskPending ? (
                <span
                  className="
                flex
                flex-col
                items-center
                gap-2
                leading-none
              "
                >
                  <span
                    className="
                  inline-flex
                  items-center
                  gap-2
                "
                  >
                    <svg
                      className="
                    h-4
                    w-4
                    animate-spin
                    text-white
                  "
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="currentColor"
                        strokeOpacity="0.35"
                        strokeWidth="3"
                      />

                      <path
                        d="M12 3a9 9 0 0 1 9 9"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                    </svg>

                    <span>{pathwayBusyLabel}</span>
                  </span>

                  <span
                    className="
                  flex
                  w-40
                  items-center
                  gap-1
                "
                    aria-hidden="true"
                  >
                    <span
                      className="
                    h-1
                    flex-1
                    animate-pulse
                    rounded-full
                    bg-white/35
                  "
                    />

                    <span
                      className="
                    h-1
                    flex-1
                    animate-pulse
                    rounded-full
                    bg-white/55
                  "
                      style={{
                        animationDelay: "0.15s",
                      }}
                    />

                    <span
                      className="
                    h-1
                    flex-1
                    animate-pulse
                    rounded-full
                    bg-white/75
                  "
                      style={{
                        animationDelay: "0.3s",
                      }}
                    />
                  </span>
                </span>
              ) : (
                "Submit Pathway Data"
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

function SkeletonCard({ className = "" }) {
  return (
    <div
      className={`rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 ${className}`}
    >
      <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
      <div className="mt-3 h-3 w-full animate-pulse rounded bg-slate-100" />
      <div className="mt-2 h-3 w-5/6 animate-pulse rounded bg-slate-100" />
      <div className="mt-2 h-3 w-4/6 animate-pulse rounded bg-slate-100" />
    </div>
  );
}
